import React, { useState } from 'react'
import { Stack, FormErrorMessage, Flex, Text, Textarea } from '@chakra-ui/core'
import { Controller, useFormContext } from 'react-hook-form'
import {
  Eye,
  EyeOff,
  Globe,
  Lock,
  Mail,
  Phone,
  Plus,
  Shield,
  SquareChartGantt,
  User,
  UserCog,
  UserRoundSearch,
} from 'lucide-react'
import permanentData from 'components/AdminSettings/permanentData'
import { CustomCheckBox } from 'components/CommonComponents/GroupsTree/style'
import { UserFormData } from 'redux/actions/admin'
import { IConfigRetailUser } from 'constants/interfaces'
import { InputWithIcon } from 'components/AdminConsole/commonUI/InputWithIcon'
import { SearchableMenu } from 'components/AdminConsole/commonUI/SearchableMenu'
import { StyledFormControl, StyledFormLabel, StyledStack } from 'components/AdminConsole/commonUI/styles'
import useLocaleText from 'components/useLocaleText'

export const {
  UID_REG_EX,
  PHONE_NUMBER_REG_EX,
  EMAIL_REG_EX,
  PASSWORD_REG_EX,
  LOCALE,
  COUNTRY_CODES_LIST,
  PERMISSIONS,
  ROLES,
} = permanentData

interface UserFormProps {
  user?: IConfigRetailUser | null
  isLoading: boolean
}

export const UserForm: React.FC<UserFormProps> = ({ user, isLoading }) => {
  const { control, watch, register, errors, clearError } = useFormContext<UserFormData>()
  const [showPassword, setShowPassword] = useState(false)
  const isEdit = !!user

  const admin_uid = useLocaleText('admin_uid')
  const admin_uid_placeholder = useLocaleText('admin_uid_placeholder')
  const admin_password = useLocaleText('admin_password')
  const admin_password_placeholder = useLocaleText('admin_password_placeholder')
  const admin_first_name = useLocaleText('admin_first_name')
  const admin_first_name_placeholder = useLocaleText('admin_first_name_placeholder')
  const admin_last_name = useLocaleText('admin_last_name')
  const admin_last_name_placeholder = useLocaleText('admin_last_name_placeholder')
  const admin_sec_uid = useLocaleText('admin_sec_uid')
  const admin_sec_uid_placeholder = useLocaleText('admin_sec_uid_placeholder')
  const admin_desc = useLocaleText('admin_desc')
  const admin_desc_placeholder = useLocaleText('admin_desc_placeholder')
  const admin_language = useLocaleText('admin_language')
  const admin_select_placeholder = useLocaleText('admin_select_placeholder')
  const admin_email = useLocaleText('admin_email')
  const admin_email_placeholder = useLocaleText('admin_email_placeholder')
  const admin_country_code = useLocaleText('admin_country_code')
  const admin_phone_number = useLocaleText('admin_phone_number')
  const admin_phone_placeholder = useLocaleText('admin_phone_placeholder')
  const admin_permission = useLocaleText('admin_permission')
  const admin_role = useLocaleText('admin_role')
  const admin_enable_mfa = useLocaleText('admin_enable_mfa')

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  return (
    <Stack spacing={8} pointerEvents={isLoading ? 'none' : 'auto'} opacity={isLoading ? 0.8 : 1}>
      <Stack spacing={4}>
        <StyledStack>
          <StyledFormControl isInvalid={!!errors.uid} w="50%">
            <StyledFormLabel required>
              <User size={18} strokeWidth={2.5} />
              <Text>{admin_uid}</Text>
            </StyledFormLabel>
            <InputWithIcon
              isDisabled={!!user}
              name="uid"
              ref={register({
                required: 'User ID is required',
                pattern: {
                  value: UID_REG_EX,
                  message: 'Invalid User ID format',
                },
              })}
              placeholder={admin_uid_placeholder}
              autoComplete="username"
            />

            <FormErrorMessage>{errors.uid && errors.uid.message}</FormErrorMessage>
          </StyledFormControl>

          <StyledFormControl isInvalid={!!errors.password} w="50%">
            <StyledFormLabel required={!isEdit}>
              <Lock size={18} strokeWidth={2.5} />
              <Text>{admin_password}</Text>
            </StyledFormLabel>
            <InputWithIcon
              name="password"
              type={showPassword ? 'text' : 'password'}
              ref={register({
                required: !isEdit ? 'Password is required' : false,
                pattern: {
                  value: PASSWORD_REG_EX,
                  message:
                    'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character',
                },
              })}
              placeholder={admin_password_placeholder}
              autoComplete="new-password"
              icon={
                showPassword ? (
                  <EyeOff size={18} onClick={togglePasswordVisibility} style={{ cursor: 'pointer' }} />
                ) : (
                  <Eye size={18} onClick={togglePasswordVisibility} style={{ cursor: 'pointer' }} />
                )
              }
            />

            <FormErrorMessage>{errors.password && errors.password.message}</FormErrorMessage>
          </StyledFormControl>
        </StyledStack>

        <StyledStack>
          <StyledFormControl isInvalid={!!errors.first_name} w="50%">
            <StyledFormLabel required>
              <User size={18} strokeWidth={2.5} />
              <Text>{admin_first_name}</Text>
            </StyledFormLabel>
            <InputWithIcon
              name="first_name"
              ref={register({ required: 'First name is required' })}
              placeholder={admin_first_name_placeholder}
            />
            <FormErrorMessage>{errors.first_name && errors.first_name.message}</FormErrorMessage>
          </StyledFormControl>

          <StyledFormControl isInvalid={!!errors.last_name} w="50%">
            <StyledFormLabel>
              <User size={18} strokeWidth={2.5} />
              <Text>{admin_last_name}</Text>
            </StyledFormLabel>

            <InputWithIcon name="last_name" ref={register} placeholder={admin_last_name_placeholder} />

            <FormErrorMessage>{errors.last_name && errors.last_name.message}</FormErrorMessage>
          </StyledFormControl>
        </StyledStack>
      </Stack>

      <Stack direction="column" spacing={4}>
        <StyledFormControl isInvalid={!!errors.sec_uid} w="calc(50% - 5px)" mb={4}>
          <StyledFormLabel>
            <Shield size={18} strokeWidth={2.5} />
            <Text>{admin_sec_uid}</Text>
          </StyledFormLabel>

          <InputWithIcon name="sec_uid" ref={register} placeholder={admin_sec_uid_placeholder} />
        </StyledFormControl>

        <StyledFormControl isInvalid={!!errors.desc}>
          <StyledFormLabel>
            <SquareChartGantt size={18} strokeWidth={2.5} />
            <Text>{admin_desc}</Text>
          </StyledFormLabel>

          <Textarea
            name="desc"
            ref={register}
            borderRadius="10px"
            resize="none"
            border="1px solid"
            placeholder={admin_desc_placeholder}
            _focus={{ boxShadow: '0px 0px 0px 1px black' }}
          />
        </StyledFormControl>
      </Stack>

      <Stack direction="column" spacing={4}>
        <StyledStack>
          <StyledFormControl isInvalid={!!errors.locale} w="50%">
            <StyledFormLabel required>
              <Globe size={18} strokeWidth={2.5} />
              <Text>{admin_language}</Text>
            </StyledFormLabel>

            <Controller
              name="locale"
              control={control}
              rules={{ required: 'Language is required' }}
              as={
                <SearchableMenu
                  isInvalid={!!errors.locale}
                  items={LOCALE}
                  selectedId={watch('locale')}
                  placeholder={admin_select_placeholder}
                  onSelect={(item) => {
                    control.setValue('locale', item.id)
                    clearError('locale')
                  }}
                />
              }
            />

            <FormErrorMessage>{errors.locale && errors.locale.message}</FormErrorMessage>
          </StyledFormControl>

          <StyledFormControl isInvalid={!!errors.email} w="50%">
            <StyledFormLabel required={!watch('phone_number') && !watch('country_code')}>
              <Mail size={18} strokeWidth={2.5} />
              <Text>{admin_email}</Text>
            </StyledFormLabel>

            <InputWithIcon
              name="email"
              type="email"
              ref={register({
                validate: (value) => {
                  const phone = watch('phone_number')
                  const code = watch('country_code')
                  if (!phone && !code && !value) return 'Email is required'
                  return true
                },
                pattern: {
                  value: EMAIL_REG_EX,
                  message: 'Invalid email address',
                },
              })}
              placeholder={admin_email_placeholder}
            />

            <FormErrorMessage>{errors.email && errors.email.message}</FormErrorMessage>
          </StyledFormControl>
        </StyledStack>

        <StyledStack>
          <StyledFormControl isInvalid={!!errors.country_code} w="50%">
            <StyledFormLabel required={!!watch('phone_number') || !watch('email')}>
              <Plus size={18} strokeWidth={2.5} />
              <Text>{admin_country_code}</Text>
            </StyledFormLabel>

            <Controller
              name="country_code"
              control={control}
              rules={{
                validate: (value) => {
                  const phone = watch('phone_number')
                  if (phone && !value) return 'Country code is required when phone number is provided'
                  return true
                },
              }}
              as={
                <SearchableMenu
                  isInvalid={!!errors.country_code}
                  items={COUNTRY_CODES_LIST.map((item) => ({ ...item, isShowId: true }))}
                  selectedId={watch('country_code') || ''}
                  placeholder={admin_select_placeholder}
                  isShowClearButton={true}
                  onSelect={(item) => {
                    control.setValue('country_code', item.id, true)
                    clearError('country_code')
                  }}
                  onClear={() => {
                    control.setValue('country_code', '', true)
                    clearError('country_code')
                  }}
                />
              }
            />

            <FormErrorMessage>{errors.country_code && errors.country_code.message}</FormErrorMessage>
          </StyledFormControl>

          <StyledFormControl isInvalid={!!errors.phone_number} w="50%">
            <StyledFormLabel required={!!watch('country_code') || !watch('email')}>
              <Phone size={18} strokeWidth={2.5} />
              <Text>{admin_phone_number}</Text>
            </StyledFormLabel>

            <InputWithIcon
              name="phone_number"
              type="number"
              ref={register({
                validate: (value) => {
                  const code = watch('country_code')
                  if (code && !value) return 'Phone number is required'
                  return true
                },
                pattern: {
                  value: PHONE_NUMBER_REG_EX,
                  message: 'Invalid phone number format',
                },
              })}
              onChange={() => {
                if (watch('phone_number') && watch('country_code')) {
                  clearError(['phone_number', 'country_code'])
                }
              }}
              placeholder={admin_phone_placeholder}
            />

            <FormErrorMessage>{errors.phone_number && errors.phone_number.message}</FormErrorMessage>
          </StyledFormControl>
        </StyledStack>

        <StyledFormControl>
          <Flex align="center" style={{ columnGap: '10px' }}>
            <CustomCheckBox
              name="enable_mfa"
              ref={register}
              isDisabled={!watch('country_code') || !watch('phone_number')}
              defaultIsChecked={false}
              color="black"
            />

            <Text fontSize="14px">{admin_enable_mfa}</Text>
          </Flex>
        </StyledFormControl>
      </Stack>

      <StyledStack>
        <StyledFormControl isInvalid={!!errors.permission} w="50%">
          <StyledFormLabel required>
            <UserCog size={18} strokeWidth={2.5} />
            <Text>{admin_permission}</Text>
          </StyledFormLabel>

          <Controller
            name="permission"
            control={control}
            rules={{ required: 'Permission is required' }}
            as={
              <SearchableMenu
                isInvalid={!!errors.permission}
                isShowSearch={false}
                items={PERMISSIONS.map((item) => ({ ...item, id: String(item.id), isShowId: false }))}
                selectedId={watch('permission')?.toString() || ''}
                placeholder={admin_select_placeholder}
                onSelect={(item) => {
                  control.setValue('permission', Number(item.id))
                  clearError('permission')
                }}
              />
            }
          />

          <FormErrorMessage>{errors.permission && errors.permission.message}</FormErrorMessage>
        </StyledFormControl>

        <StyledFormControl isInvalid={!!errors.user_group_role} w="50%">
          <StyledFormLabel required>
            <UserRoundSearch size={18} strokeWidth={2.5} />
            <Text>{admin_role}</Text>
          </StyledFormLabel>

          <Controller
            name="user_group_role"
            control={control}
            rules={{ required: 'Role is required' }}
            as={
              <SearchableMenu
                isInvalid={!!errors.user_group_role}
                isShowSearch={false}
                items={ROLES.map((item) => ({ ...item, id: String(item.id), isShowId: false }))}
                selectedId={watch('user_group_role')?.toString() || ''}
                placeholder={admin_select_placeholder}
                onSelect={(item) => {
                  control.setValue('user_group_role', Number(item.id))
                  clearError('user_group_role')
                }}
              />
            }
          />

          <FormErrorMessage>{errors.user_group_role && errors.user_group_role.message}</FormErrorMessage>
        </StyledFormControl>
      </StyledStack>
    </Stack>
  )
}
